import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { superAdmin as api } from "@/lib/api/super";
import type { SuperAdminRow } from "@/lib/api/super";
import { ApiError } from "@/lib/api/transport";
import { useAuth } from "./useAuth";

export type { SuperAdminRow, SuperAdminSource } from "@/lib/api/super";

export type SuperAdminsStatus = "idle" | "loading" | "ready" | "error";

export type SuperAdminsErrorCode = "cannot_revoke_self" | "unknown";

export class SuperAdminsFailed extends Error {
  readonly code: SuperAdminsErrorCode;
  constructor(code: SuperAdminsErrorCode) {
    super(code);
    this.code = code;
  }
}

const KNOWN: ReadonlySet<string> = new Set(["cannot_revoke_self"]);

function tagApiError(err: unknown): never {
  if (err instanceof ApiError) {
    const code = (err.body as { error?: string } | null)?.error;
    throw new SuperAdminsFailed(
      code && KNOWN.has(code) ? (code as SuperAdminsErrorCode) : "unknown",
    );
  }
  throw err;
}

export interface SuperAdminsContextValue {
  status: SuperAdminsStatus;
  error: Error | null;
  admins: readonly SuperAdminRow[];
  grant: (userId: number) => Promise<void>;
  revoke: (userId: number) => Promise<void>;
  impersonate: (userId: number) => Promise<void>;
  reload: () => Promise<void>;
}

const SuperAdminsContext = createContext<SuperAdminsContextValue>(null!);

export function useSuperAdmins(): SuperAdminsContextValue {
  return useContext(SuperAdminsContext);
}

function useSuperAdminsProvider(): SuperAdminsContextValue {
  const { user, status: authStatus } = useAuth();
  const [admins, setAdmins] = useState<readonly SuperAdminRow[]>([]);
  const [status, setStatus] = useState<SuperAdminsStatus>("idle");
  const [error, setError] = useState<Error | null>(null);

  const reload = useCallback(
    () =>
      api
        .fetchSuperAdmins()
        .then(({ admins: rows }) => {
          setAdmins(rows);
          setStatus("ready");
          setError(null);
        })
        .catch((e: Error) => {
          setError(e);
          setStatus("error");
        }),
    [],
  );

  useEffect(() => {
    if (authStatus !== "authenticated" || !user?.admin) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- single-shot load on auth flip
    setStatus("loading");
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStatus, user?.admin]);

  const grant = useCallback(async (userId: number) => {
    const { user: row } = await api.grantAdmin(userId).catch(tagApiError);
    setAdmins((prev) =>
      prev.some((a) => a.id === row.id)
        ? prev.map((a) => (a.id === row.id ? row : a))
        : [...prev, row],
    );
  }, []);

  const revoke = useCallback(async (userId: number) => {
    const { user: row } = await api.revokeAdminRole(userId).catch(tagApiError);
    setAdmins((prev) =>
      row.admin
        ? prev.map((a) => (a.id === row.id ? row : a))
        : prev.filter((a) => a.id !== row.id),
    );
  }, []);

  const impersonate = useCallback(async (userId: number) => {
    await api.startImpersonation(userId).catch(tagApiError);
    window.location.assign("/workspace");
  }, []);

  return useMemo(
    () => ({ status, error, admins, grant, revoke, impersonate, reload }),
    [status, error, admins, grant, revoke, impersonate, reload],
  );
}

export function SuperAdminsProvider({ children }: { children: ReactNode }) {
  const value = useSuperAdminsProvider();
  return (
    <SuperAdminsContext.Provider value={value}>
      {children}
    </SuperAdminsContext.Provider>
  );
}
